import { apiConnection } from "@/services/api-connection";
import { mailStatusConstantes } from "@/constantes/mailStatus";
import { sendReportConstantes } from "@/constantes/sendReport";

const URL_MAIL_UNBLOCK = "/app/admin/mail/unblock";

class MailStatusManager {
  /**
   * Lève manuellement le blocage d'une adresse email.
   *
   * Le déblocage est identifié par l'adresse et non par un utilisateur : le blocage porte sur la
   * boîte mail (cf. MailBounce::$email et MailSuppressionService::isSuppressed() côté back), et
   * cet écran comme le rapport d'envoi peuvent afficher des adresses sans utilisateur rattaché.
   */
  async unblock(email) {
    return await apiConnection.post(URL_MAIL_UNBLOCK, { email: this.normalize(email) });
  }

  normalize(email) {
    return String(email ?? "").toLowerCase();
  }

  /**
   * Repasse un statut de boîte mail à « Valide » après un déblocage manuel.
   *
   * L'objet est muté en place : c'est la même référence que celle affichée par l'écran appelant,
   * qui ne se reconstruit pas de lui-même après un déblocage.
   */
  markAsValid(mailStatus) {
    if (!mailStatus) {
      return mailStatus;
    }

    mailStatus.status = mailStatusConstantes.VALID;
    mailStatus.blockedAt = null;
    mailStatus.cause = null;

    return mailStatus;
  }

  isBlocked(status) {
    return status === mailStatusConstantes.TEMPORARY_BLOCK || status === mailStatusConstantes.DEFINITIVE_BLOCK;
  }

  /**
   * Un statut VALID peut porter un incident qui n'a pas atteint le seuil de suppression (ex :
   * un unique bounce "reputation", cf. MailBlockStatusService::buildView() côté back,
   * ST143-TA-4709). `cause`/`blockedAt` sont alors renseignés bien que `status` reste VALID —
   * sans ce garde-fou, l'incident redevient invisible dans les écrans qui n'affichent que
   * le statut brut.
   */
  hasIncident(mailStatus) {
    if (!mailStatus || this.isBlocked(mailStatus.status)) {
      return false;
    }

    const cause = mailStatus.cause;

    return Boolean(mailStatus.blockedAt || cause?.dsn || cause?.category || cause?.code);
  }

  getStatusColor(status, hasIncident = false) {
    switch (status) {
      case mailStatusConstantes.TEMPORARY_BLOCK:
        return "warning";
      case mailStatusConstantes.DEFINITIVE_BLOCK:
        return "danger";
      default:
        return hasIncident ? "warning" : "success";
    }
  }

  getStatusIcon(status, hasIncident = false) {
    switch (status) {
      case mailStatusConstantes.TEMPORARY_BLOCK:
        return "clock";
      case mailStatusConstantes.DEFINITIVE_BLOCK:
        return "ban";
      default:
        return hasIncident ? "triangle-exclamation" : "check";
    }
  }

  getStatusLabelKey(status, hasIncident = false) {
    if (hasIncident && !this.isBlocked(status)) {
      return "mail_status.status.valid_with_incident";
    }

    switch (status) {
      case mailStatusConstantes.TEMPORARY_BLOCK:
        return "mail_status.status.temporary_block";
      case mailStatusConstantes.DEFINITIVE_BLOCK:
        return "mail_status.status.definitive_block";
      default:
        return "mail_status.status.valid";
    }
  }

  getTitleKey(mailStatus) {
    if (this.isBlocked(mailStatus?.status)) {
      return "mail_status.modal.title";
    }

    return this.hasIncident(mailStatus) ? "mail_status.modal.title_incident" : "mail_status.modal.title_valid";
  }

  /**
   * L'API sérialise parfois le statut d'envoi en chaîne ("1") : sans normalisation, les
   * comparaisons strictes échoueraient toutes et un rapport pourtant envoyé basculerait sur la
   * variante "unknown".
   */
  normalizeSendStatus(sendStatus) {
    const isNumeric = typeof sendStatus === "number" || (typeof sendStatus === "string" && sendStatus.trim() !== "");
    const status = isNumeric ? Number(sendStatus) : NaN;

    return Number.isInteger(status) ? status : null;
  }

  /**
   * Décision partagée par les quatre écrans (liste, les deux fiches détail et le rapport d'envoi
   * des identifiants) pour le point de statut d'envoi (colonne/ligne "Statut", cf.
   * SendReport::$status) : STATUS_BLOCKED est vérifié explicitement, sans dépendre du statut de
   * boîte mail (mailStatus), absent des écrans StudeaManager et de toute façon porteur de l'état
   * *actuel* de la boîte, pas de la raison pour laquelle ce rapport précis a été bloqué.
   *
   * STATUS_ERROR a sa propre variante : un échec de transport SMTP n'a pas la même cause qu'un
   * blocage lié à l'adresse du destinataire, même s'ils partagent la couleur rouge.
   */
  getSendDotVariant(sendStatus, mailStatus = null) {
    const status = this.normalizeSendStatus(sendStatus);

    if (status === sendReportConstantes.STATUS_IN_PREPARATION) {
      return "pending";
    }
    if (status === sendReportConstantes.STATUS_BLOCKED) {
      return "danger";
    }
    if (status === sendReportConstantes.STATUS_ERROR) {
      return "error";
    }
    if (this.isBlocked(mailStatus?.status)) {
      return "danger";
    }
    if (this.hasIncident(mailStatus)) {
      return "warning";
    }
    if (status === sendReportConstantes.STATUS_SENT) {
      return "success";
    }

    return "unknown";
  }

  getSendDotClass(variant) {
    switch (variant) {
      case "pending":
        return "-pending";
      case "unknown":
        return "bg-secondary";
      case "error":
        return "bg-danger";
      default:
        return "bg-" + variant;
    }
  }

  getSendDotTooltipKey(variant) {
    switch (variant) {
      case "pending":
        return "mail_status.send_dot.pending";
      case "danger":
        return "mail_status.send_dot.blocked";
      case "error":
        return "mail_status.send_dot.error";
      case "warning":
        return "mail_status.send_dot.incident";
      case "unknown":
        return "mail_status.send_dot.unknown";
      default:
        return "mail_status.send_dot.sent";
    }
  }
}

export const mailStatusManager = new MailStatusManager();
